import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { 
  ArrowLeft, Upload, FileText, Mic, BarChart, 
  AlertCircle, CheckCircle, Clock, Users, Activity, Link, MessageSquare, BarChart3, Bug
} from 'lucide-react'
import { KnowledgeInput } from '../components/management/KnowledgeInput'
import { KnowledgeGaps } from '../components/management/KnowledgeGaps'
import { KnowledgeConfidence } from '../components/management/KnowledgeConfidence'
import { Statistics } from '../components/management/Statistics'
import { DocumentList } from '../components/management/DocumentList'
import { MonitoringDashboard } from '../components/management/MonitoringDashboard'
import { ChunkReview } from '../components/management/ChunkReview'
import { ChatPromptsManager } from '../components/management/ChatPromptsManager'
import { UserManagement } from '../components/management/UserManagement'
import { ChatDebugger } from '../components/management/ChatDebugger'

export function ManagementPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  
  // Get initial tab from URL or default to 'knowledge'
  const getInitialTab = () => {
    const tabFromUrl = searchParams.get('tab')
    const validTabs = ['knowledge', 'gaps', 'confidence', 'stats', 'documents', 'monitoring', 'prompts', 'users', 'debug']
    if (tabFromUrl && validTabs.includes(tabFromUrl)) {
      return tabFromUrl as any
    }
    return 'knowledge'
  }
  
  const [activeTab, setActiveTab] = useState<'knowledge' | 'gaps' | 'confidence' | 'stats' | 'documents' | 'monitoring' | 'prompts' | 'users' | 'debug'>(getInitialTab())
  const [reviewDocumentId, setReviewDocumentId] = useState<string | null>(null)
  
  // Update URL when tab changes
  const handleTabChange = (newTab: string) => {
    setActiveTab(newTab as any)
    setSearchParams({ tab: newTab })
  }
  
  // Handle browser back/forward buttons
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab')
    const validTabs = ['knowledge', 'gaps', 'confidence', 'stats', 'documents', 'monitoring', 'prompts', 'users', 'debug']
    if (tabFromUrl && validTabs.includes(tabFromUrl) && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl as any)
    }
  }, [searchParams])

  const tabs = [
    { id: 'knowledge', label: 'Wissen hinzufügen', icon: Upload },
    { id: 'documents', label: 'Dokumentenverwaltung', icon: FileText },
    { id: 'gaps', label: 'Gemeldete Wissenslücken', icon: AlertCircle },
    { id: 'prompts', label: 'Chat-Vorschläge', icon: MessageSquare },
    { id: 'users', label: 'Benutzerverwaltung', icon: Users },
    { id: 'debug', label: 'Chat Debugging', icon: Bug },
    { id: 'confidence', label: 'Wissensqualität', icon: CheckCircle, disabled: true },
    { id: 'stats', label: 'Statistiken', icon: BarChart, disabled: true },
    { id: 'monitoring', label: 'System Monitor', icon: Activity, disabled: true },
  ]

  return (
    <div className="min-h-screen bg-gray-950">
      {/* Header */}
      <header className="glass border-b border-white/10 px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/')}
              className="p-2 hover:bg-white/10 rounded-lg transition-smooth"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-xl font-bold">Management Portal</h1>
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigate('/kpi-demo')}
              className="btn btn-secondary flex items-center space-x-2"
            >
              <BarChart3 className="w-4 h-4" />
              <span>KPIs</span>
            </button>
            <button
              onClick={() => navigate('/analytics')}
              className="btn btn-primary flex items-center space-x-2"
            >
              <BarChart className="w-4 h-4" />
              <span>Analytics Dashboard</span>
            </button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex space-x-1 p-1 glass rounded-xl mb-8">
          {tabs.map((tab) => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => !tab.disabled && handleTabChange(tab.id)}
                className={`flex-1 flex items-center justify-center space-x-2 py-3 px-4 rounded-lg transition-all relative ${
                  tab.disabled 
                    ? 'opacity-50 cursor-not-allowed'
                    : activeTab === tab.id
                      ? 'bg-primary-600 text-white'
                      : 'hover:bg-white/10'
                }`}
                disabled={tab.disabled}
              >
                <div className="flex items-center space-x-2">
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  <span className="hidden md:inline">
                    {tab.id === 'documents' ? (
                      <div className="text-center leading-tight">
                        <div>Dokumente</div>
                        <div className="text-xs opacity-90">verwalten</div>
                      </div>
                    ) : tab.id === 'users' ? (
                      <div className="text-center leading-tight">
                        <div>Benutzer</div>
                        <div className="text-xs opacity-90">verwaltung</div>
                      </div>
                    ) : (
                      tab.label
                    )}
                  </span>
                </div>
                {tab.disabled && (
                  <span className="absolute -top-2 -right-2 text-xs bg-yellow-600 text-white px-2 py-0.5 rounded-full">
                    In Entwicklung
                  </span>
                )}
              </button>
            )
          })}
        </div>

        {/* Content */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
        >
          {activeTab === 'knowledge' && <KnowledgeInput />}
          {activeTab === 'documents' && (
            <>
              {reviewDocumentId ? (
                <ChunkReview 
                  documentId={reviewDocumentId} 
                  onClose={() => setReviewDocumentId(null)} 
                />
              ) : (
                <DocumentList 
                  onOpenChunkReview={(docId) => setReviewDocumentId(docId)} 
                />
              )}
            </>
          )}
          {activeTab === 'gaps' && <KnowledgeGaps />}
          {activeTab === 'prompts' && <ChatPromptsManager />}
          {activeTab === 'users' && <UserManagement />}
          {activeTab === 'debug' && <ChatDebugger />}
          {activeTab === 'confidence' && <KnowledgeConfidence />}
          {activeTab === 'stats' && <Statistics />}
          {activeTab === 'monitoring' && <MonitoringDashboard />}
        </motion.div>
      </div>
    </div>
  )
}